import React from "react";
import type { BrandSummary, CampaignReportResponse } from "../../types";
import type { ExportTemplateModule } from "../types";
import { coerceNumber, formatCurrency, formatNumber } from "../utils";

interface BrandComparisonModuleProps {
  data: CampaignReportResponse;
  module: ExportTemplateModule<"brand-comparison">;
}

const BRAND_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4"];

export const BrandComparisonModule: React.FC<BrandComparisonModuleProps> = ({
  data,
  module,
}) => {
  const limit = module.props?.limit || 4;
  const brands = [...(data.brands ?? [])]
    .sort((a, b) => coerceNumber(b.total_sales) - coerceNumber(a.total_sales))
    .slice(0, limit);

  if (!brands.length) {
    return (
      <section>
        {module.title && (
          <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
            {module.title}
          </div>
        )}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6 text-slate-500 text-sm">
          Multi-brand data unavailable.
        </div>
      </section>
    );
  }

  return (
    <section>
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
          {module.title}
        </div>
      )}
      <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${Math.min(brands.length, 4)}, 1fr)` }}>
        {brands.map((brand, index) => (
          <BrandCard
            key={brand.brand}
            brand={brand}
            color={BRAND_COLORS[index % BRAND_COLORS.length]}
          />
        ))}
      </div>
    </section>
  );
};

function BrandCard({ brand, color }: { brand: BrandSummary; color: string }) {
  const roas = coerceNumber(brand.roas);
  const metrics = [
    { label: "Sales", value: formatCurrency(brand.total_sales, { maximumFractionDigits: 0 }) },
    { label: "Spend", value: formatCurrency(brand.total_spend, { maximumFractionDigits: 0 }) },
    { label: "ROAS", value: `${roas.toFixed(2)}x` },
    { label: "Units", value: formatNumber(brand.total_units, { maximumFractionDigits: 0 }) },
    { label: "Clicks", value: formatNumber(brand.total_clicks, { maximumFractionDigits: 0 }) },
    { label: "Campaigns", value: String(brand.campaign_count) },
  ];

  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span
          className="inline-block h-3 w-3 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-lg font-semibold text-slate-900 truncate">
          {brand.brand}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {metrics.map((metric) => (
          <div key={metric.label}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              {metric.label}
            </div>
            <div className="text-base font-semibold text-slate-900">
              {metric.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
